import React from 'react';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { Illustration } from '@photosi/rn-components/illustration/Illustration';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import braze from 'src/_shared/braze';
import { useStore } from 'src/contexts/StoreContext';
import { SourcesEventType } from '../../ModalUploadMultisource.constants';
import { ILLUSTRATION_NAMES } from '@photosi/rn-components/illustration/illustration.helper';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { newStyles, styles } from './PickerComputerUploadContent.styles';
import { useNavigation } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { DynamicModalProps } from '../../../DynamicModal.types';

type PickerComputerUploadContentProps = {
  isDragging: boolean;
  groupCode?: string;
  selectedCard?: string;
} & Pick<DynamicModalProps['dynamicModalLayer'], 'uploadMultiSourceCallback'>;

export const PickerComputerUploadContent = observer((props: PickerComputerUploadContentProps) => {
  const {
    i18n,
    dynamicModal: { closeModal },
    toastStore,
    project: { activeProject },
  } = useStore();
  const { isDragging, uploadMultiSourceCallback, groupCode, selectedCard } = props;
  const { isMobile } = useResponsiveScreen();
  const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;
  const groupTypes = activeProject?.groupTypes ?? [];
  const currentGroup = groupTypes.find(group => group.code === groupCode);
  const labelButtonAdd = !currentGroup?.minItems
    ? selectedCard
      ? i18n.t('upload:choose-max', { photos: selectedCard?.replace('x', '') })
      : i18n.t('upload:choose')
    : i18n.t('upload:choose-with-minimum', { photos: currentGroup?.minItems });

  const onPressHandler = async () => {
    const productImages: PickedImage[] = await pickImages({
      allowsMultipleSelection: true,
      i18n,
      errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
        if (areAllPhotosInvalid) {
          closeModal();
        }
        toastStore.showErrorToast({
          id: 'alert-uploading-photos-error-toast',
          message: errorMessage,
        });
      },
      newRoute: newRoute,
    });
    if (!!productImages.length) {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
        source_type: SourcesEventType.Computer,
      });
      braze.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick);

      if (newRoute) {
        //const autoselect = activeProject!.thumbnailImages.length == 0;
        productImages.forEach(productImage => (productImage.source = SELECTED_MENU_ITEMS.Computer));
        await activeProject?.addImages(productImages);
      } else {
        uploadMultiSourceCallback({ files: productImages, sourceType: SourcesEventType.Computer });
      }

      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
        n_photo: productImages.length,
        source_type: SourcesEventType.Computer,
      });
    }
  };

  return (
    <View style={newStyles.details}>
      <View style={{ width: 100, height: 100, opacity: isDragging ? 0.3 : 1 }}>
        <Illustration name={ILLUSTRATION_NAMES.Import} />
      </View>
      <View style={newStyles.textWrapper}>
        {!isMobile && <TextLato style={styles.title}>{i18n.t('upload:upload-or-drag')}</TextLato>}
        {!newRoute && (
          <TextLato style={styles.text}>
            {isMobile ? i18n.t('upload:description-mobile') : i18n.t('upload:description')}
          </TextLato>
        )}
      </View>
      <View style={newStyles.button}>
        <PrimaryButton
          innerStyle={{ opacity: isDragging ? 0.3 : 1 }}
          onPress={onPressHandler}
          testID={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE)}
          focusId={modalTestID(TestIDs.CHOOSE_PHOTO, TestIDs.CHOOSE)}
          focusGroup={FocusGroups.MODAL}
          focusOrder={0}
          text={labelButtonAdd}
          textStyle={styles.buttonText}
          size={BUTTON_SIZES.REGULAR}
          type={BUTTON_TYPES.DEFAULT}
          layout={BUTTON_LAYOUTS.FILL}
        />
      </View>
    </View>
  );
});
